export const EXAM_LOAD_ERROR_CODES = {
  HTTP_ERROR: 'HTTP_ERROR',
  NETWORK_ERROR: 'NETWORK_ERROR',
  INVALID_JSON: 'INVALID_JSON',
} as const;

export type ExamLoadErrorCode = (typeof EXAM_LOAD_ERROR_CODES)[keyof typeof EXAM_LOAD_ERROR_CODES];

export interface ExamLoadErrorState {
  code?: ExamLoadErrorCode;
  status?: number;
}

export interface ExamLoadErrorDetails {
  message: string;
  hint?: string;
}

/**
 * Keeps only the non-sensitive fields required to select a user-facing message.
 * The original error remains available to the caller for console diagnostics.
 */
export function normalizeExamLoadError(error: unknown): ExamLoadErrorState {
  if (!error || typeof error !== 'object') {
    return {};
  }

  const source = error as Record<string, unknown>;
  const knownCodes = Object.values(EXAM_LOAD_ERROR_CODES) as string[];
  const code = knownCodes.includes(source.code as string)
    ? (source.code as ExamLoadErrorCode)
    : undefined;
  const status =
    typeof source.status === 'number' && Number.isInteger(source.status)
      ? source.status
      : undefined;

  return {
    ...(code ? { code } : {}),
    ...(status !== undefined ? { status } : {}),
  };
}

export function getExamLoadErrorDetails({
  code,
  status,
}: ExamLoadErrorState): ExamLoadErrorDetails {
  if (code === EXAM_LOAD_ERROR_CODES.HTTP_ERROR) {
    if (status === 401 || status === 403) {
      return {
        message:
          'O link pode ter expirado, ser inválido ou não estar mais disponível. Solicite um novo link ou tente novamente.',
      };
    }

    if (status === 404) {
      return {
        message: 'O exame não foi encontrado ou o link não está mais disponível.',
        hint: 'Verifique se você recebeu o link correto.',
      };
    }

    if (status !== undefined && status >= 500) {
      return {
        message: 'O serviço do exame está temporariamente indisponível.',
        hint: 'Aguarde alguns instantes e tente novamente.',
      };
    }
  }

  if (code === EXAM_LOAD_ERROR_CODES.NETWORK_ERROR) {
    return {
      message: 'Não foi possível acessar os dados do exame.',
      hint: 'Verifique sua conexão com a internet e tente novamente.',
    };
  }

  if (code === EXAM_LOAD_ERROR_CODES.INVALID_JSON) {
    return {
      message: 'Os dados recebidos para este exame são inválidos.',
      hint: 'Solicite um novo link e tente novamente.',
    };
  }

  return {
    message: 'Não foi possível carregar os dados deste exame.',
    hint: 'Tente novamente ou solicite um novo link.',
  };
}
